import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Icon, Section, Card } from "@/components/primitives";
import { BadgeChip, badgeLabel, _badgeKindLabel } from "@/components/BadgeChip";
import { BadgeDetailModal } from "@/components/BadgeDetailModal";
import { useAuth } from "@/hooks/useAuth";
import { useUserBadges, isLegacy, type UserBadge, type BadgeKind } from "@/hooks/useUserBadges";
import { useCollectorTier } from "@/hooks/useCollectorTier";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";

// ─── Locked placeholder copy ─────────────────────────────────────────────────
// Display names + "how to earn" hints for the locked-row UI. Keep aligned
// with badge_thresholds (SQL) and BadgeChip variant maps (TS). Drift here
// produces wrong labels but never breaks the awards logic.

interface VariantInfo { name: string; how: string }

const KIND_INFO: Record<BadgeKind, { name: string; how: string }> = {
  collector_tier:     { name: "Collector Tier",     how: "Climbs as you verify items — Sprout, Picker, Rack Runner, Archivist, Conservator." },
  era_milestone:      { name: "Era Specialist",     how: "Verify items from a specific decade." },
  brand_authority:    { name: "Brand Authority",    how: "Verify 20+ items of a single brand." },
  diverse_collector:  { name: "Time Traveler",      how: "Verify items across 4+ different eras." },
  region_collector:   { name: "Region Collector",   how: "Verify items from brands of a specific origin." },
  style_collector:    { name: "Style Collector",    how: "Verify items in a specific style family." },
  deadstock_collector:{ name: "It's Deadstock",     how: "Verify 3+ unworn, tags-attached items." },
  community_streak:   { name: "Community",          how: "Engage with FIG daily — scan, note, follow." },
  fotw_winner:        { name: "Find of the Week",   how: "Win the weekly Find of the Week vote." },
  fotw_candidate:     { name: "FotW Candidate",     how: "Get nominated for Find of the Week." },
  founder:            { name: "Founder",            how: "Awarded to FIG's founding cohort — closed once the cohort fills." },
  verified_moderator: { name: "Verified Moderator", how: "By invitation. Trusted reviewers who help moderate FIG." },
  era_specialist:     { name: "Era Specialist",     how: "(Legacy badge — no new awards.)" },
};

const ERA_MILESTONE_VARIANTS: VariantInfo[] = [
  { name: "1930s Specialist", how: "Verify 5+ items from the 1930s." },
  { name: "1940s Specialist", how: "Verify 5+ items from the 1940s." },
  { name: "1950s Specialist", how: "Verify 10+ items from the 1950s." },
  { name: "1960s Specialist", how: "Verify 20+ items from the 1960s." },
  { name: "1970s Specialist", how: "Verify 20+ items from the 1970s." },
  { name: "1980s Specialist", how: "Verify 20+ items from the 1980s." },
  { name: "1990s Specialist", how: "Verify 20+ items from the 1990s." },
  { name: "2000s Specialist", how: "Verify 20+ items from the 2000s." },
  { name: "2010s Specialist", how: "Verify 20+ items from the 2010s." },
];
const ERA_MILESTONE_KEYS = ["1930s","1940s","1950s","1960s","1970s","1980s","1990s","2000s","2010s"];

const REGION_VARIANTS: { key: string; info: VariantInfo }[] = [
  { key: "hawaii_5o",        info: { name: "Hawaii 5-O",       how: "Verify 3+ items from Hawaiian brands." } },
  { key: "harajuku_hunter",  info: { name: "Harajuku Hunter",  how: "Verify 3+ items from Japanese brands." } },
  { key: "happy_in_paris",   info: { name: "Happy in Paris",   how: "Verify 3+ items from French brands." } },
  { key: "wild_west",        info: { name: "Wild West",        how: "Verify 3+ items from Western-wear brands." } },
  { key: "italian_mobster",  info: { name: "Italian Mobster",  how: "Verify 3+ items from Italian brands." } },
  { key: "tea_and_vintage",  info: { name: "Tea & Vintage",    how: "Verify 3+ items from British brands." } },
];

const STYLE_VARIANTS: { key: string; info: VariantInfo }[] = [
  { key: "gossip_girl",         info: { name: "Gossip Girl",         how: "Verify 3+ items from preppy / ivy brands." } },
  { key: "sports_connoisseur",  info: { name: "Sports Connoisseur",  how: "Verify 5+ items from sportswear brands." } },
  { key: "from_the_streets",    info: { name: "From the Streets",    how: "Verify 5+ items from streetwear brands." } },
];

const STREAK_VARIANTS: { key: string; info: VariantInfo }[] = [
  { key: "bestie",        info: { name: "Bestie",        how: "14 consecutive days of activity (scans, notes, loves, follows)." } },
  { key: "cheerleader",   info: { name: "Cheerleader",   how: "Leave 25+ supportive notes on other users' scans." } },
  { key: "photo_pro",     info: { name: "Photo Pro",     how: "10 consecutive PASS verdicts in a row." } },
  { key: "stack_master",  info: { name: "Stack Master",  how: "Verify 5+ items in a single 24-hour period." } },
  { key: "grail_granter", info: { name: "Grail Granter", how: "Scan an item that fulfills another collector's grail." } },
];

const DIVERSE_VARIANTS: { key: string; info: VariantInfo }[] = [
  { key: "time_traveler", info: { name: "Time Traveler", how: "Verify items across 4+ different eras." } },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

function groupBy<T, K extends string | number>(
  arr: T[],
  key: (t: T) => K,
): Record<K, T[]> {
  const out = {} as Record<K, T[]>;
  for (const item of arr) {
    const k = key(item);
    (out[k] ||= []).push(item);
  }
  return out;
}

/**
 * /you/badges — owner-only curation page.
 *
 * Sections (in render order):
 *   1. Collector tier card (always-on summary)
 *   2. Collection milestones — era_milestone (per decade), brand_authority,
 *      diverse_collector/time_traveler, deadstock_collector
 *   3. Affinities — region_collector (6 variants), style_collector (3)
 *   4. Community — community_streak (5 variants), fotw_winner, fotw_candidate
 *   5. Cohort — founder, verified_moderator
 *   6. Legacy — era_specialist (only when user has one)
 */
export default function Badges() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { badges, featured, isLoading, invalidate } = useUserBadges(user?.id, {
    includeExpired: false,
  });
  const { tier, hint } = useCollectorTier(user?.id);

  const [detail, setDetail] = useState<UserBadge | null>(null);
  const [tierDetail, setTierDetail] = useState(false);
  const [saving, setSaving] = useState(false);

  const featuredIds = new Set(featured.map((b) => b.id));

  const toggleFeatured = async (badge: UserBadge) => {
    if (saving) return;
    const isOn = featuredIds.has(badge.id);
    const nextIds = isOn
      ? featured.filter((b) => b.id !== badge.id).map((b) => b.id)
      : [...featured.map((b) => b.id), badge.id];

    if (nextIds.length > 3) {
      toast.error("You can feature up to 3 badges. Unfeature one first.");
      return;
    }

    setSaving(true);
    const { error } = await supabase.rpc("set_featured_badges", {
      badge_ids: nextIds,
    });
    setSaving(false);
    if (error) {
      toast.error(error.message ?? "Couldn't save featured badges");
      return;
    }
    invalidate();
    toast.success(isOn ? "Unfeatured." : "Featured.");
  };

  const sections = groupBy(badges, (b) => b.kind);
  const hasAnyBadges = badges.length > 0;
  const hasAnyTier = !!tier;
  const hasLegacy = (sections.era_specialist?.length ?? 0) > 0;

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100%", position: "relative" }}>
        {/* Top bar */}
        <div style={{
          padding: "12px 20px 0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <button
            onClick={() => nav(-1)}
            aria-label="Back"
            style={{
              background: "none", border: "1px solid hsl(var(--ink))",
              padding: 6, cursor: "pointer", color: "hsl(var(--ink))",
            }}
          >
            {Icon.chev(16, "l")}
          </button>
          <span className="caps">Badges</span>
          <div style={{ width: 30 }} />
        </div>

        <div style={{ padding: "20px 20px 40px" }}>
          {/* Page intro */}
          <h1 style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontWeight: 800, fontSize: 28, lineHeight: 1.1,
            color: "hsl(var(--ink))", margin: "0 0 6px",
          }}>
            Your badges
          </h1>
          <p style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 14, lineHeight: 1.5,
            color: "hsl(var(--ink-2))", margin: "0 0 18px",
          }}>
            Pick up to three earned badges to feature on your profile.
            Locked rows show what's still in the set to collect.
          </p>

          {/* Tier card */}
          {hasAnyTier && (
            <Section pad={0}>
              <Card style={{ padding: 16, marginBottom: 14 }}>
                <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 4 }}>
                  Collector tier
                </div>
                <button
                  type="button"
                  onClick={() => setTierDetail(true)}
                  style={{
                    background: "transparent", border: "none", padding: 0,
                    cursor: "pointer", textAlign: "left",
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontWeight: 800, fontSize: 24, lineHeight: 1.1,
                    color: "hsl(var(--ink))",
                  }}
                >
                  {tier}
                </button>
                {hint && (
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontStyle: "italic", fontSize: 13,
                    color: "hsl(var(--ink-2))",
                    marginTop: 6,
                  }}>
                    {hint}
                  </div>
                )}
              </Card>
            </Section>
          )}

          {/* Empty state — no tier AND no badges */}
          {!hasAnyTier && !hasAnyBadges && !isLoading && (
            <Card style={{ padding: 18, marginBottom: 14, textAlign: "center" }}>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 15, lineHeight: 1.5,
                color: "hsl(var(--ink-2))",
              }}>
                Start building your closet — your first verified scan unlocks the Collector tier.
              </div>
            </Card>
          )}

          {/* ── Section: Collection milestones ────────────────────────── */}
          <BadgeSection
            title="Collection milestones"
            specs={[
              { kind: "era_milestone",
                variantKeys: ERA_MILESTONE_KEYS,
                variantField: "era",
                variantInfo: (key) => {
                  const idx = ERA_MILESTONE_KEYS.indexOf(key);
                  return ERA_MILESTONE_VARIANTS[idx] ?? null;
                } },
              { kind: "brand_authority" },   // variant-less; multiple instances OK
              { kind: "diverse_collector",
                variantKeys: DIVERSE_VARIANTS.map((v) => v.key),
                variantField: "variant",
                variantInfo: (key) => DIVERSE_VARIANTS.find((v) => v.key === key)?.info ?? null },
              { kind: "deadstock_collector" },
            ]}
            byKind={sections}
            featuredIds={featuredIds}
            onToggle={toggleFeatured}
            onOpenDetail={setDetail}
            saving={saving}
          />

          {/* ── Section: Affinities (region + style) ──────────────────── */}
          <BadgeSection
            title="Affinities"
            specs={[
              { kind: "region_collector",
                variantKeys: REGION_VARIANTS.map((v) => v.key),
                variantField: "variant",
                variantInfo: (key) => REGION_VARIANTS.find((v) => v.key === key)?.info ?? null },
              { kind: "style_collector",
                variantKeys: STYLE_VARIANTS.map((v) => v.key),
                variantField: "variant",
                variantInfo: (key) => STYLE_VARIANTS.find((v) => v.key === key)?.info ?? null },
            ]}
            byKind={sections}
            featuredIds={featuredIds}
            onToggle={toggleFeatured}
            onOpenDetail={setDetail}
            saving={saving}
          />

          {/* ── Section: Community ────────────────────────────────────── */}
          <BadgeSection
            title="Community"
            specs={[
              { kind: "community_streak",
                variantKeys: STREAK_VARIANTS.map((v) => v.key),
                variantField: "variant",
                variantInfo: (key) => STREAK_VARIANTS.find((v) => v.key === key)?.info ?? null },
              { kind: "fotw_winner" },
              { kind: "fotw_candidate" },
            ]}
            byKind={sections}
            featuredIds={featuredIds}
            onToggle={toggleFeatured}
            onOpenDetail={setDetail}
            saving={saving}
          />

          {/* ── Section: Cohort ───────────────────────────────────────── */}
          <BadgeSection
            title="Cohort"
            specs={[
              { kind: "founder" },
              { kind: "verified_moderator" },
            ]}
            byKind={sections}
            featuredIds={featuredIds}
            onToggle={toggleFeatured}
            onOpenDetail={setDetail}
            saving={saving}
          />

          {/* ── Section: Legacy (conditional) ─────────────────────────── */}
          {hasLegacy && (
            <BadgeSection
              title="Legacy"
              specs={[{ kind: "era_specialist" }]}
              byKind={sections}
              featuredIds={featuredIds}
              onToggle={toggleFeatured}
              onOpenDetail={setDetail}
              saving={saving}
              hideLocked
            />
          )}
        </div>

        {detail && (
          <BadgeDetailModal badge={detail} onClose={() => setDetail(null)} />
        )}
        {tierDetail && (
          <BadgeDetailModal
            badge={null}
            tierName={tier}
            tierHint={hint}
            onClose={() => setTierDetail(false)}
          />
        )}
      </div>
    </MobileFrame>
  );
}

// ─── BadgeSection — variant-aware locked-placeholder generator ─────────────

interface KindSpec {
  kind: BadgeKind;
  /** When set, each entry produces its own locked row. */
  variantKeys?: string[];
  /** Which metadata field discriminates variants (era / variant). */
  variantField?: "era" | "variant";
  variantInfo?: (key: string) => VariantInfo | null;
}

interface BadgeSectionProps {
  title: string;
  specs: KindSpec[];
  byKind: Partial<Record<BadgeKind, UserBadge[]>>;
  featuredIds: Set<string>;
  onToggle: (b: UserBadge) => void;
  onOpenDetail: (b: UserBadge) => void;
  saving: boolean;
  /** Suppress locked-row generation (used for the Legacy section). */
  hideLocked?: boolean;
}

function BadgeSection({
  title,
  specs,
  byKind,
  featuredIds,
  onToggle,
  onOpenDetail,
  saving,
  hideLocked,
}: BadgeSectionProps) {
  type Row =
    | { kind: "earned"; badge: UserBadge }
    | { kind: "locked"; badgeKind: BadgeKind; variantKey?: string; info: { name: string; how: string } };

  const rows: Row[] = [];

  for (const spec of specs) {
    const earned = byKind[spec.kind] ?? [];

    if (spec.variantKeys && spec.variantField) {
      // Variant-aware: emit one row per variant — earned if held, locked otherwise.
      const earnedByVariant = new Map<string, UserBadge>();
      for (const b of earned) {
        const v = String((b.metadata as Record<string, unknown>)?.[spec.variantField] ?? "");
        if (v) earnedByVariant.set(v, b);
      }
      for (const key of spec.variantKeys) {
        const heldBadge = earnedByVariant.get(key);
        if (heldBadge) {
          rows.push({ kind: "earned", badge: heldBadge });
        } else if (!hideLocked) {
          const info = spec.variantInfo?.(key) ?? { name: key, how: "" };
          rows.push({ kind: "locked", badgeKind: spec.kind, variantKey: key, info });
        }
      }
    } else {
      // Variant-less: render every earned instance + one locked row if none.
      if (earned.length > 0) {
        for (const b of earned) rows.push({ kind: "earned", badge: b });
      } else if (!hideLocked) {
        const info = KIND_INFO[spec.kind];
        rows.push({ kind: "locked", badgeKind: spec.kind, info });
      }
    }
  }

  if (rows.length === 0) return null;

  return (
    <div style={{ marginBottom: 16 }}>
      <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>
        {title}
      </div>
      <Card style={{ padding: 4 }}>
        {rows.map((row, i) => {
          const borderTop = i > 0 ? "1px dashed hsl(var(--sand-deep))" : "none";

          if (row.kind === "earned") {
            const b = row.badge;
            const legacy = isLegacy(b.kind);
            return (
              <div
                key={b.id}
                style={{
                  display: "flex", alignItems: "center", gap: 12,
                  padding: "10px 12px",
                  borderTop,
                }}
              >
                <BadgeChip badge={b} size="sm" onClick={() => onOpenDetail(b)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif",
                    fontWeight: 700, fontSize: 14,
                    color: "hsl(var(--ink))",
                  }}>
                    {badgeLabel(b)}
                  </div>
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
                    color: "hsl(var(--ink-2))",
                    marginTop: 2,
                  }}>
                    {_badgeKindLabel(b.kind)}
                  </div>
                </div>
                {!legacy && (
                  <button
                    type="button"
                    onClick={() => onToggle(b)}
                    disabled={saving}
                    aria-pressed={featuredIds.has(b.id)}
                    style={{
                      background: featuredIds.has(b.id)
                        ? "hsl(var(--ink))"
                        : "transparent",
                      color: featuredIds.has(b.id)
                        ? "hsl(var(--ink-fg))"
                        : "hsl(var(--ink))",
                      border: "1px solid hsl(var(--ink))",
                      padding: "6px 10px",
                      cursor: saving ? "wait" : "pointer",
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
                      opacity: saving ? 0.6 : 1,
                      flexShrink: 0,
                    }}
                  >
                    {featuredIds.has(b.id) ? "✓ Featured" : "Feature"}
                  </button>
                )}
              </div>
            );
          }

          // Locked / not-yet-earned placeholder
          return (
            <div
              key={`locked-${row.badgeKind}-${row.variantKey ?? "default"}`}
              style={{
                display: "flex", alignItems: "center", gap: 12,
                padding: "10px 12px",
                borderTop,
                opacity: 0.6,
              }}
            >
              <div style={{
                display: "inline-flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                gap: 1, minWidth: 64, padding: "6px 10px",
                background: "transparent",
                color: "hsl(var(--ink-2))",
                border: "1.5px dashed hsl(var(--ink-2))",
                borderRadius: 14,
                fontFamily: "'IBM Plex Mono', monospace",
                textAlign: "center", lineHeight: 1.05,
                flexShrink: 0,
              }}>
                <span style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontWeight: 700, fontSize: 11,
                  color: "hsl(var(--ink-2))",
                  whiteSpace: "nowrap",
                }}>
                  Locked
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontWeight: 700, fontSize: 14,
                  color: "hsl(var(--ink))",
                }}>
                  {row.info.name}
                </div>
                <div style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 9, letterSpacing: "0.12em", textTransform: "uppercase",
                  color: "hsl(var(--ink-2))",
                  marginTop: 2,
                }}>
                  Not yet earned
                </div>
                <div style={{
                  fontFamily: "'Fraunces', 'Playfair Display', serif",
                  fontSize: 12, lineHeight: 1.4,
                  color: "hsl(var(--ink-2))",
                  marginTop: 4,
                  fontStyle: "italic",
                }}>
                  {row.info.how}
                </div>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}
